import { ReactNode, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';

import {
  AgencyKnowledgeCategory,
  AgencyKnowledgeDto,
  CATEGORY_LABELS,
  CreateAgencyKnowledgePayload,
  UpdateAgencyKnowledgePayload,
} from '../types';
import { KnowledgeHelpModal } from './KnowledgeHelpModal';

interface Props {
  open: boolean;
  entry?: AgencyKnowledgeDto | null;
  existingTags: string[];
  onClose: () => void;
  onCreate: (payload: CreateAgencyKnowledgePayload) => Promise<void>;
  onUpdate: (id: string, payload: UpdateAgencyKnowledgePayload) => Promise<void>;
}

const DEFAULT_FORM = {
  title: '',
  category: 'destination' as AgencyKnowledgeCategory,
  content: '',
  tags: [] as string[],
  isActive: true,
};

/** Renderiza markdown básico sin librerías externas */
function MarkdownPreview({ content }: { content: string }) {
  if (!content.trim()) {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic">
        El preview aparece acá mientras escribís...
      </Typography>
    );
  }

  const lines = content.split('\n');
  return (
    <Box>
      {lines.map((line, i) => {
        if (line.startsWith('# ')) {
          return (
            <Typography key={i} variant="h6" gutterBottom mt={i > 0 ? 1.5 : 0}>
              {line.slice(2)}
            </Typography>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <Typography key={i} variant="subtitle1" fontWeight="bold" gutterBottom mt={i > 0 ? 1 : 0}>
              {line.slice(3)}
            </Typography>
          );
        }
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return (
            <Box key={i} sx={{ display: 'flex', gap: 1, ml: 1 }}>
              <Typography variant="body2">•</Typography>
              <Typography variant="body2">{renderInline(line.slice(2))}</Typography>
            </Box>
          );
        }
        if (line.trim() === '') {
          return <Box key={i} mb={0.75} />;
        }
        return (
          <Typography key={i} variant="body2">
            {renderInline(line)}
          </Typography>
        );
      })}
    </Box>
  );
}

function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          part
        ),
      )}
    </>
  );
}

export function AgencyKnowledgeEditor({
  open,
  entry,
  existingTags,
  onClose,
  onCreate,
  onUpdate,
}: Props) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const isEdit = Boolean(entry);

  useEffect(() => {
    if (open) {
      setForm(
        entry
          ? {
              title: entry.title,
              category: entry.category,
              content: entry.content,
              tags: entry.tags,
              isActive: entry.isActive,
            }
          : DEFAULT_FORM,
      );
    }
  }, [open, entry]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setLoading(true);
    try {
      if (isEdit && entry) {
        await onUpdate(entry.id, form);
      } else {
        await onCreate(form);
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEdit ? 'Editar entrada' : 'Nueva entrada de conocimiento'}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 520 }}>
            {/* Columna izquierda — formulario */}
            <Box sx={{ p: 3, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Título"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  fullWidth
                  required
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={form.category}
                    label="Categoría"
                    onChange={(e) =>
                      setForm((f) => ({ ...f, category: e.target.value as typeof form.category }))
                    }
                  >
                    {(Object.entries(CATEGORY_LABELS) as [typeof form.category, string][]).map(
                      ([val, label]) => (
                        <MenuItem key={val} value={val}>
                          {label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Stack>

              <Autocomplete
                multiple
                freeSolo
                options={existingTags}
                value={form.tags}
                onChange={(_, newValue) =>
                  setForm((f) => ({
                    ...f,
                    tags: newValue.map((t) => t.toLowerCase().trim()),
                  }))
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Tags"
                    placeholder="Ej: porto-de-galinhas, brasil, playa"
                    helperText="Presioná Enter para agregar. Usá minúsculas y guiones."
                  />
                )}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Contenido (Markdown)
                  </Typography>
                  <Tooltip title="Ver guía y ejemplos">
                    <IconButton size="small" onClick={() => setHelpOpen(true)}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Activo
                  </Typography>
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    size="small"
                  />
                </Stack>
              </Box>

              <TextField
                multiline
                minRows={14}
                maxRows={22}
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                fullWidth
                placeholder={`## Cómo llegar\nVuelo a Recife (REC), transfer ~1h...\n\n## Alojamiento\n- **Parejas:** Tabajuba (bungalows íntimos)\n- **Familias:** Summerville (pool, actividades)`}
                sx={{ flexGrow: 1, '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </Box>

            {/* Columna derecha — preview */}
            <Box sx={{ p: 3, overflowY: 'auto', background: 'background.default' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Preview
              </Typography>
              <MarkdownPreview content={form.content} />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleSubmit}
            disabled={!form.title.trim() || !form.content.trim()}
          >
            {isEdit ? 'Guardar cambios' : 'Crear entrada'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <KnowledgeHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
